/**
 * Offline Detection Utility
 *
 * Provides methods to detect if the app is offline and if the user is reading
 * from offline storage.
 */

/**
 * Check if the browser is offline
 */
export function isNetworkOffline(): boolean {
  return !navigator.onLine;
}

/**
 * Check if we're in offline reading mode
 * This is true when:
 * 1. Network is offline, OR
 * 2. API requests are failing (detected by checking if we have offline book data)
 */
export async function isOfflineMode(bookId?: string): Promise<boolean> {
  // First check: network status
  if (isNetworkOffline()) {
    return true;
  }

  // If no bookId provided, just return network status
  if (!bookId) {
    return false;
  }

  // Second check: try to detect if we're using offline data
  // This is a lightweight check - we can expand it if needed
  try {
    // Import here to avoid circular dependencies
    const { offlineContentService } = await import('../services/offline/OfflineContentService');
    const offlineBook = await offlineContentService.getOfflineBook(bookId);

    // If we have offline data and network fails, we're likely in offline mode
    // But if network is online, we're in online mode even if offline data exists
    return offlineBook !== undefined && isNetworkOffline();
  } catch {
    // If offline service fails, assume we're online
    return false;
  }
}

/**
 * Hook-friendly version for React components
 * Usage: const offline = await isOfflineMode(bookId);
 */
export { isOfflineMode as checkOfflineMode };
